import React, { useEffect, useState } from "react";
import { FormikProvider, useFormik, Form, FieldArray, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Modal, Row, Col, Button } from "react-bootstrap";
import { VscDiffAdded } from "react-icons/vsc";
import { BiTrash } from "react-icons/bi";
import { FaEdit, FaEye, FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";
import CommonFormHeading from "../CommonUtils/CommonFormHeading";
import { ImagePdfBucket } from "../CommonUtils/ImagesBase64";
import { CommonAxiosGet, CommonAxiosPost } from "../CommonUtils/CommonAxios";
import { failureAlert, successAlert2 } from "../CommonUtils/SweetAlerts";
import { config } from "../CommonUtils/CommonURLS";

const sliderImageValidationSchema = Yup.object().shape({
    slidingImagesEntryList: Yup.array().of(
        Yup.object().shape({
            image: Yup.string().required("Image is required"),
            imageDescription: Yup.string().required("Description is required"),
        })
    ),
});

const createEmptySliderImageRow = () => ({
    image: "",
    imageDescription: "",
});

const buildSliderImageUrl = (imagePath) => {
    if (!imagePath) {
        return "";
    }

    if (/^https?:\/\//i.test(imagePath)) {
        return imagePath;
    }

    return `${config.url.FILE_DOWNLOAD_URL}${imagePath}`;
};

export default function SlidingImages() {
    const [isSliderModalOpen, setIsSliderModalOpen] = useState(false);
    const [sliderImageRecords, setSliderImageRecords] = useState([]);
    const [isLoadingSliderRecords, setIsLoadingSliderRecords] = useState(true);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [selectedPreviewImage, setSelectedPreviewImage] = useState("");
    const [isSlidingImageEditMode, setIsSlidingImageEditMode] = useState(false);
    const [selectedSlidingImageRecordId, setSelectedSlidingImageRecordId] = useState(null);
    const [reuploadedSlidingImageRows, setReuploadedSlidingImageRows] = useState({});

    const sliderImagesFormik = useFormik({
        initialValues: {
            slidingImagesEntryList: [createEmptySliderImageRow()],
        },
        validationSchema: sliderImageValidationSchema,
        onSubmit: (sliderFormValues, { resetForm }) => {
            Swal.fire({
                text: "Are you sure you want to submit?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, submit it!",
                cancelButtonText: "No, cancel!",
                backdrop: false,
            }).then((result) => {
                if (!result.isConfirmed) {
                    return;
                }

                const slidingImagesSubmitUrl = isSlidingImageEditMode
                    ? `${config.url.STU_URL}updateSlidingImages`
                    : `${config.url.STU_URL}saveSlidingImagesEntry`;

                const slidingImagesSubmitPayload = isSlidingImageEditMode
                    ? {
                        slidingImagesEntryList: sliderFormValues.slidingImagesEntryList.map((slidingImageFormRow) => ({
                            sno: selectedSlidingImageRecordId,
                            image: slidingImageFormRow.image,
                            imageDescription: slidingImageFormRow.imageDescription,
                        })),
                    }
                    : sliderFormValues;

                CommonAxiosPost(slidingImagesSubmitUrl, slidingImagesSubmitPayload).then((response) => {
                    if (response?.data?.scode === "01") {
                        successAlert2(response?.data?.sdesc);
                        resetForm({ values: { slidingImagesEntryList: [createEmptySliderImageRow()] } });
                        setIsSliderModalOpen(false);
                        setIsSlidingImageEditMode(false);
                        setSelectedSlidingImageRecordId(null);
                        loadSlidingImages();
                    } else {
                        failureAlert(response?.data?.sdesc || "Unable to save sliding images");
                    }
                });
            });
        },
    });

    const loadSlidingImages = () => {
        setIsLoadingSliderRecords(true);
        const sliderFetchUrl = `${config.url.STU_URL}ov-public/getSlidingImages`;

        CommonAxiosGet(sliderFetchUrl).then((response) => {
            if (response?.data?.scode === "01") {
                setSliderImageRecords(response?.data?.data || []);
            } else {
                setSliderImageRecords([]);
            }
        }).finally(() => {
            setIsLoadingSliderRecords(false);
        });
    };

    useEffect(() => {
        loadSlidingImages();
    }, []);

    const handleOpenSliderModal = () => {
        setIsSlidingImageEditMode(false);
        setSelectedSlidingImageRecordId(null);
        setReuploadedSlidingImageRows({});
        sliderImagesFormik.resetForm({ values: { slidingImagesEntryList: [createEmptySliderImageRow()] } });
        setIsSliderModalOpen(true);
    };

    const handleCloseSliderModal = () => {
        setIsSlidingImageEditMode(false);
        setSelectedSlidingImageRecordId(null);
        setReuploadedSlidingImageRows({});
        sliderImagesFormik.resetForm({ values: { slidingImagesEntryList: [createEmptySliderImageRow()] } });
        setIsSliderModalOpen(false);
    };

    const handleEditSlidingImageRecord = (slidingImageRecord) => {
        const slidingImageRecordId = slidingImageRecord?.sno || null;

        setIsSlidingImageEditMode(true);
        setSelectedSlidingImageRecordId(slidingImageRecordId);
        setReuploadedSlidingImageRows({});
        sliderImagesFormik.setValues({
            slidingImagesEntryList: [
                {
                    image: slidingImageRecord?.image || "",
                    imageDescription: slidingImageRecord?.imageDescription || "",
                },
            ],
        });
        setIsSliderModalOpen(true);
    };

    const handleDeleteSlidingImageRecord = (slidingImageRecordId) => {
        if (!slidingImageRecordId) {
            return;
        }

        Swal.fire({
            title: "Delete Sliding Image",
            text: "Are you sure you want to delete this record?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "Cancel",
            backdrop: false,
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }

            const slidingImageDeleteUrl = `${config.url.STU_URL}deleteSlidingImagesEntry?sno=${slidingImageRecordId}`;

            CommonAxiosPost(slidingImageDeleteUrl).then((response) => {
                if (response?.data?.scode === "01") {
                    successAlert2(response?.data?.sdesc || response?.data?.data);
                    loadSlidingImages();
                } else {
                    failureAlert(response?.data?.sdesc || response?.data?.data || "Unable to delete sliding image");
                }
            });
        });
    };

    const handleOpenPreviewModal = (imagePath) => {
        if (!imagePath) {
            return;
        }

        setSelectedPreviewImage(imagePath);
        setIsPreviewModalOpen(true);
    };

    const handleClosePreviewModal = () => {
        setSelectedPreviewImage("");
        setIsPreviewModalOpen(false);
    };

    const renderSlidingImagesFormTable = () => (
        <div className="table-responsive">
            <table className="table table-condensed table-bordered table-sm affl-table">
                <thead>
                    <tr>
                        <th>S.No</th>
                        <th>Upload Image</th>
                        <th>Description</th>
                        {!isSlidingImageEditMode && <th>Action</th>}
                        {/* <th>Action</th> */}
                    </tr>
                </thead>
                <tbody>
                    <FieldArray name="slidingImagesEntryList">
                        {({ push, remove }) => (
                            <>
                                {sliderImagesFormik.values.slidingImagesEntryList.map((sliderFormRow, sliderRowIndex) => {
                                    const previewImageUrl = buildSliderImageUrl(sliderFormRow.image);
                                    const isSlidingImageReuploaded = Boolean(reuploadedSlidingImageRows[sliderRowIndex]);

                                    return (
                                        <tr key={sliderRowIndex}>
                                            <td className="text-center align-middle">{sliderRowIndex + 1}</td>
                                            <td>
                                                {isSlidingImageEditMode && previewImageUrl && !isSlidingImageReuploaded ? (
                                                    <div className="mb-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-success btn-sm"
                                                            onClick={() => handleOpenPreviewModal(previewImageUrl)}
                                                        >
                                                            <FaEye className="me-1" />
                                                            View Existing Image
                                                        </button>
                                                    </div>
                                                ) : null}
                                                <div className="d-flex align-items-center gap-2 mt-3 mb-2">
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        accept="image/png,image/jpeg,image/jpg"
                                                        onChange={(event) => {
                                                            const sliderFieldName = `slidingImagesEntryList[${sliderRowIndex}].image`;

                                                            setReuploadedSlidingImageRows((previousRows) => ({
                                                                ...previousRows,
                                                                [sliderRowIndex]: true,
                                                            }));

                                                            ImagePdfBucket(
                                                                event,
                                                                sliderImagesFormik,
                                                                "/Uploads/slidingImages/",
                                                                sliderFieldName,
                                                                `sliding_image_${sliderRowIndex}`,
                                                                "image"
                                                            );
                                                        }}
                                                    />
                                                    {previewImageUrl && (!isSlidingImageEditMode || isSlidingImageReuploaded) ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-success btn-sm"
                                                            onClick={() => handleOpenPreviewModal(previewImageUrl)}
                                                        >
                                                            <FaEye />
                                                        </button>
                                                    ) : null}
                                                </div>
                                                <ErrorMessage
                                                    name={`slidingImagesEntryList[${sliderRowIndex}].image`}
                                                    component="div"
                                                    className="text-error"
                                                />
                                            </td>
                                            <td>
                                                <textarea
                                                    rows={2}
                                                    className="form-control"
                                                    name={`slidingImagesEntryList[${sliderRowIndex}].imageDescription`}
                                                    value={sliderImagesFormik.values.slidingImagesEntryList[sliderRowIndex].imageDescription}
                                                    onChange={sliderImagesFormik.handleChange}
                                                    placeholder="Enter image description"
                                                />
                                                <ErrorMessage
                                                    name={`slidingImagesEntryList[${sliderRowIndex}].imageDescription`}
                                                    component="div"
                                                    className="text-error"
                                                />
                                            </td>
                                            {!isSlidingImageEditMode && (
                                                <td align="center" className="align-middle">
                                                    {sliderRowIndex === 0 && (
                                                        <button
                                                            type="button"
                                                            className=" btn btn-sm btn-success"
                                                            onClick={() => push(createEmptySliderImageRow())}
                                                        >
                                                            <VscDiffAdded />
                                                        </button>
                                                    )}
                                                    {sliderRowIndex >= 1 && (
                                                        <button
                                                            type="button"
                                                            className=" btn btn-sm btn-danger"
                                                            onClick={() => remove(sliderRowIndex)}
                                                        >
                                                            <BiTrash />
                                                        </button>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </>
                        )}
                    </FieldArray>
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="affl-newdiv">
            <div className="container-fluid px-1 mt-3">
                <CommonFormHeading heading="Sliding Images" />
                <div className="row mb-3">
                    <div className="col-12">
                        <Row>
                            <Col xs={12} sm={12} md={12} lg={9} xl={9} xxl={9} />
                            <Col xs={12} sm={12} md={12} lg={3} xl={3} xxl={3}>
                                <div className="d-flex flex-row justify-content-end">
                                    {sliderImageRecords.length > 0 && (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="primary"
                                            onClick={handleOpenSliderModal}
                                        >
                                            + Add Sliding Images
                                        </Button>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </div>
                </div>

                {isLoadingSliderRecords ? (
                    <div className="container shadow-none px-3 m-0 mw-100 pb-5" style={{ backgroundColor: "#FAF7F0" }}>
                        <div className="text-center py-4">Loading...</div>
                    </div>
                ) : sliderImageRecords.length === 0 ? (
                    <div className="container shadow-none px-3 m-0 mw-100 pb-5" style={{ backgroundColor: "#FAF7F0" }}>
                        <FormikProvider value={sliderImagesFormik}>
                            <Form onSubmit={sliderImagesFormik.handleSubmit}>
                                {renderSlidingImagesFormTable()}
                                <div className="d-flex justify-content-end mt-2">
                                    <Button type="submit" size="sm" variant="success">
                                        Submit
                                    </Button>
                                </div>
                            </Form>
                        </FormikProvider>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-condensed table-bordered table-sm table-responsive table-stripped affl-table">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Preview</th>
                                    <th>Description</th>
                                    <th>Edit / Delete</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sliderImageRecords.map((slidingImageRecord, sliderIndex) => {
                                    const slidingImageRecordId = slidingImageRecord?.sno || null;
                                    const sliderImageSource = buildSliderImageUrl(slidingImageRecord?.image || "");

                                    return (
                                        <tr key={slidingImageRecordId || sliderIndex}>
                                            <td>{sliderIndex + 1}</td>
                                            <td className="text-center">
                                                {sliderImageSource ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-success btn-sm"
                                                        onClick={() => handleOpenPreviewModal(sliderImageSource)}
                                                    >
                                                        <FaEye />
                                                        {/* View */}
                                                    </button>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td className="">
                                                {slidingImageRecord?.image_description || "-"}
                                            </td>
                                            <td className="text-center align-middle">
                                                <button type="button" className="btn btn-sm btn-primary me-2">
                                                    <FaEdit

                                                        title="Edit"
                                                        onClick={() => handleEditSlidingImageRecord(slidingImageRecord)}
                                                    />
                                                </button>
                                                <button type="button" className="btn btn-sm btn-danger">
                                                    <FaTrash

                                                        title="Delete"
                                                        onClick={() => handleDeleteSlidingImageRecord(slidingImageRecordId)}
                                                    />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <Modal show={isSliderModalOpen} onHide={handleCloseSliderModal} size="xl" centered className="inner-modalnew">
                    <Modal.Header closeButton>
                        <Modal.Title>{isSlidingImageEditMode ? "Edit Sliding Images" : "Add Sliding Images"}</Modal.Title>
                    </Modal.Header>
                    <FormikProvider value={sliderImagesFormik}>
                        <Form onSubmit={sliderImagesFormik.handleSubmit}>
                            <Modal.Body>
                                {renderSlidingImagesFormTable()}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" size="sm" onClick={handleCloseSliderModal}>
                                    Cancel
                                </Button>
                                <Button type="submit" variant="success" size="sm">
                                    {isSlidingImageEditMode ? "Update" : "Submit"}
                                </Button>
                            </Modal.Footer>
                        </Form>
                    </FormikProvider>
                </Modal>

                <Modal show={isPreviewModalOpen} onHide={handleClosePreviewModal} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>View Image</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-center">
                        {selectedPreviewImage ? (
                            <img
                                src={selectedPreviewImage}
                                alt="Sliding image preview"
                                className="img-fluid"
                                style={{ maxHeight: "70vh", objectFit: "contain" }}
                            />
                        ) : null}
                    </Modal.Body>
                </Modal>
            </div>
        </div>
    );
}